import { Socket, Channel } from 'phoenix';
import type { PagePayload } from './page-tracker.js';
import { currentPageContext } from './page-tracker.js';

/**
 * Builds the customerData socket param. Only non-empty strings are included to
 * prevent JS undefined from being serialized as the string "undefined".
 */
export function buildCustomerData(
  config: Pick<SocketConfig, 'customerEmail' | 'customerName' | 'companyName' | 'companyId' | 'customerType'>
): Record<string, string> | undefined {
  const fields: Array<[string, string | undefined]> = [
    ['email', config.customerEmail],
    ['name', config.customerName],
    ['company_name', config.companyName],
    ['company_id', config.companyId],
    ['customer_type', config.customerType],
  ];
  const data: Record<string, string> = {};
  for (const [key, value] of fields) {
    if (typeof value === 'string' && value !== '') data[key] = value;
  }
  return Object.keys(data).length > 0 ? data : undefined;
}

export interface SocketConfig {
  apiUrl: string;
  siteId: string;
  userType: 'customer' | 'agent';
  customerId?: string;
  customerEmail?: string;
  customerName?: string;
  customerType?: string;
  companyName?: string;
  companyId?: string;
  guestId?: string; // Anonymous guest tracking ID
  widgetKey?: string;
  browserLanguage?: string; // Browser language for auto-translations (e.g., 'ja', 'de')
  debug?: boolean;
}

export interface SocketMessage {
  id?: string;
  msg_id?: number;
  conversationId: string;
  conv_id?: number;
  text: string;
  body_src?: string;
  author: 'customer' | 'agent' | 'bot';
  author_id?: string;
  author_name?: string;
  author_avatar_url?: string | null;
  author_initials?: { letters: string; color: string };
  timestamp: Date;
  inserted_at?: string;
  attachments?: any[];
  // Email threading support
  delivery_channel?: 'websocket' | 'email' | 'api';
  email_sent_at?: string;
  email_delivered?: boolean;
  email_message_id?: string;
  page_visit_id?: number | null;
  // Translation support
  translation?: {
    translatedText: string;
    targetLanguage: string;
    sourceLanguage: string;
    translatedBy?: string;
    translationService?: string;
  };
  body_translated?: string;
  source_language?: string;
  target_language?: string;
  isTemporary?: boolean;
  originalText?: string;
  // Chatbot message types
  message_type?: 'text' | 'quick_replies' | 'carousel' | 'transfer';
  metadata?: {
    transfer_notice?: boolean;
    options?: Array<{
      id: string;
      label: string;
      value: string;
      next_node?: string;
    }>;
    cards?: Array<{
      id: string;
      title: string;
      description?: string;
      image_url?: string;
      buttons?: Array<{
        id: string;
        label: string;
        type: 'select' | 'url';
        value: string;
        next_node?: string;
      }>;
    }>;
  };
}

export interface ConversationData {
  conversationId: string;
  conv_id?: number;
  status?: string;
  // Email threading support
  channel_type?: 'chat' | 'email' | 'hybrid';
  email_reply_to?: string;
  // Assigned agent info
  assigned_agent_id?: number | null;
  assigned_agent_name?: string | null;
  assigned_agent_avatar_url?: string | null;
  assigned_agent_initials?: { letters: string; color: string } | null;
  // Messages
  messages: SocketMessage[];
  hasMore: boolean;
  nextCursor?: string;
}

/**
 * Assigned Agent data received when agent is assigned to conversation
 */
export interface AssignedAgentData {
  agent_id: number;
  name: string;
  avatar_url?: string | null;
  initials?: { letters: string; color: string };
}

export interface TranslationUpdate {
  msgId: string;
  messageId?: number;
  convId: string;
  translation: {
    translatedText: string;
    targetLanguage: string;
    sourceLanguage: string;
    translatedBy?: string;
    translationService?: string;
  };
  translatedText?: string;
}

/**
 * Agent Availability Events
 */
export interface AgentAvailabilityEvent {
  type: string;
  message: string;
  siteId?: string;
  nextAvailability?: string;
  agents?: Array<{
    id: number;
    name: string;
    avatar_url?: string | null;
    initials?: { letters: string; color: string };
  }>;
}

/**
 * Language Variant configuration for multilingual widget support
 */
export interface LanguageVariant {
  language_code: string;
  url_patterns: string[];
  is_default: boolean;
  is_auto_generated?: boolean; // True if generated by AI translation
  welcome_message?: string;
  header_title?: string;
  input_placeholder?: string;
  offline_title?: string;
  offline_subtitle?: string;
  offline_message?: string;
  email_label?: string;
  email_placeholder?: string;
  name_label?: string;
  name_placeholder?: string;
  message_label?: string;
  message_placeholder?: string;
  submit_button?: string;
  success_title?: string;
  success_message?: string;
  bh_next_opening?: string;
  bh_next_opening_local?: string;
  bh_hours_label?: string;
  bh_closed?: string;
  bh_timezone_note?: string;
  // System translations for status messages, errors, etc.
  system_translations?: Record<string, string>;
}

/**
 * Auto-translation status returned when joining site channel
 */
export interface AutoTranslationStatus {
  language_code: string;
  status: 'ready' | 'generating' | 'fallback';
  variant?: LanguageVariant;
  job_id?: string;
  reason?: string;
}

/**
 * Phoenix Channels communication manager for widget
 * Manages WebSocket connection using Phoenix Channels with focus on:
 * - Reliable connection management
 * - Channel joining and leaving
 * - Real-time message updates
 * - Translation synchronization
 */
export class SocketManager {
  private socket: Socket | null = null;
  private siteChannel: Channel | null = null;
  private conversationChannel: Channel | null = null;
  private config: SocketConfig;
  private conversationId: string | null = null;
  private isConnected: boolean = false;
  private isInSiteChannel: boolean = false;
  private isInConversationChannel: boolean = false;
  private connectionAttempts: number = 0;
  private maxConnectionAttempts: number = 5;
  private pageTrackingEnabled: boolean = true;
  private lastSiteViewUrl: string | null = null;

  // Event handlers - can be set by the widget
  public onConversationCreated?: (data: ConversationData) => void;
  public onConversationRestored?: (data: ConversationData) => void;
  public onMessageReceived?: (message: SocketMessage) => void;
  public onTranslationUpdated?: (update: TranslationUpdate) => void;
  public onContentUpdated?: (data: { msgId: string; messageId: number; convId: string; content: string }) => void;
  public onTranslationDeleted?: (data: { msgId: string; convId: string }) => void;
  public onConnectionStateChange?: (connected: boolean) => void;
  public onError?: (error: any) => void;
  public onConversationChannelJoined?: () => void;

  // Agent Availability handlers
  public onAgentAvailable?: (data: AgentAvailabilityEvent) => void;
  public onAgentsOffline?: (data: AgentAvailabilityEvent) => void;
  public onAgentAssigned?: (data: AssignedAgentData) => void;

  // Site config handler - called when site channel join returns config
  public onSiteConfigReceived?: (config: {
    widget_language: string;
    welcome_message?: string;
    offline_mode?: string;
    working_hours_enabled?: boolean;
    typing_preview_enabled?: boolean;
    widget_open_on?: 'click' | 'hover';
    language_variants?: LanguageVariant[];
    auto_translations_enabled?: boolean;
    auto_translation_status?: AutoTranslationStatus;
    privacy_patterns?: string[];
    privacy_param_blacklist?: string[];
    page_tracking_enabled?: boolean;
    business_hours_display?: {
      timezone: string;
      schedule: Array<{
        day_of_week: number;
        open_time: string | null;
        close_time: string | null;
        is_closed: boolean;
      }>;
      next_opening: string | null;
    } | null;
  }) => void;

  // Auto-translation ready handler - called when AI-generated translations are ready
  public onAutoTranslationReady?: (data: {
    language_code: string;
    translations: LanguageVariant;
  }) => void;

  constructor(config: SocketConfig) {
    this.config = config;
    this.log('SocketManager initialized with config:', config);
  }

  /**
   * Connect to Phoenix WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket && this.isConnected) {
        this.log('Already connected, skipping');
        resolve();
        return;
      }

      this.log('Connecting to WebSocket...');
      this.connectionAttempts++;

      // Parse WebSocket URL from API URL
      const wsUrl = this.config.apiUrl.replace(/^http/, 'ws');
      const socketPath = `${wsUrl}/socket`;

      this.log('WebSocket URL:', socketPath);

      // Build customerData for identified customers (non-empty strings only)
      const hasCustomerData = buildCustomerData(this.config);

      this.log('Connection params:', {
        widgetKey: this.config.widgetKey,
        siteId: this.config.siteId,
        customerId: this.config.customerId,
        guestId: this.config.guestId,
        customerData: hasCustomerData,
      });

      // Create Phoenix Socket with connection params
      // Note: locale is NOT sent - server uses site.widget_language setting from agent panel
      this.socket = new Socket(socketPath, {
        params: {
          widgetKey: this.config.widgetKey,
          siteId: this.config.siteId,
          customerId: this.config.customerId,
          guestId: this.config.guestId,
          customerData: hasCustomerData,
        },
        logger: this.config.debug ? ((kind, msg, data) => {
          this.log(`Phoenix ${kind}:`, msg, data);
        }) : undefined,
      });

      // Connection event handlers
      this.socket.onOpen(() => {
        this.log('✅ Socket connected');
        this.isConnected = true;
        this.connectionAttempts = 0;
        this.onConnectionStateChange?.(true);

        // Join site channel
        this.joinSiteChannel();
        resolve();
      });

      this.socket.onError((error) => {
        this.log('❌ Socket error:', error);
        this.onError?.(error);
        if (this.connectionAttempts === 1) {
          // Only reject on first attempt
          reject(error);
        }
      });

      this.socket.onClose(() => {
        this.log('🔌 Socket closed');
        this.isConnected = false;
        this.isInSiteChannel = false;
        this.isInConversationChannel = false;
        this.onConnectionStateChange?.(false);
      });

      // Connect socket
      this.socket.connect();

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!this.isConnected) {
          this.log('❌ Connection timeout');
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  /**
   * Join site channel
   * Sends browser_language param if configured for auto-translation
   */
  private joinSiteChannel(): void {
    if (!this.socket) {
      this.log('Cannot join site channel: socket not initialized');
      return;
    }

    const channelName = `site:${this.config.siteId}`;
    this.log(`Joining site channel: ${channelName}`);

    // Build join params - include browser_language for auto-translations
    const joinParams: Record<string, any> = {};
    if (this.config.browserLanguage) {
      joinParams.browser_language = this.config.browserLanguage;
      this.log('🌐 Sending browser_language:', this.config.browserLanguage);
    }

    this.siteChannel = this.socket.channel(channelName, joinParams);

    this.siteChannel
      .join()
      .receive('ok', (response) => {
        this.log('✅ Joined site channel', response);
        this.isInSiteChannel = true;

        // Notify widget about site config (including widget_language)
        if (response?.site_config) {
          this.log('📦 Received site config:', response.site_config);
          this.onSiteConfigReceived?.(response.site_config);
        }
      })
      .receive('error', (error) => {
        this.log('❌ Failed to join site channel:', error);
        this.onError?.(error);
      });

    // Listen for agent availability events
    this.siteChannel.on('agent:available', (payload) => {
      this.log('Agent became available', payload);
      this.onAgentAvailable?.({
        type: 'agent_available',
        message: 'An agent is now available',
        siteId: this.config.siteId,
        agents: (payload && (payload as any).agents) || undefined,
      });
    });

    this.siteChannel.on('agents:offline', (payload) => {
      this.log('All agents offline', payload);
      this.onAgentsOffline?.({
        type: 'agents_offline',
        message: 'No agents are currently available',
        siteId: this.config.siteId,
        agents: (payload && (payload as any).agents) || [],
      });
    });

    // Listen for auto-generated translations ready
    this.siteChannel.on('translations:ready', (payload) => {
      this.log('🌐 Auto-translation ready:', payload);
      this.onAutoTranslationReady?.({
        language_code: payload.language_code,
        translations: payload.translations,
      });
    });
  }

  /**
   * Create a new conversation
   */
  createConversation(params: Record<string, any> = {}): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.siteChannel) {
        this.log('Cannot create conversation: not in site channel');
        reject(new Error('Not in site channel'));
        return;
      }

      let paramsWithUserAgent = {
        ...params,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      };

      if (this.pageTrackingEnabled && typeof window !== 'undefined') {
        paramsWithUserAgent = {
          ...paramsWithUserAgent,
          ...currentPageContext(),
        };
      } else {
        delete paramsWithUserAgent.page_url;
        delete paramsWithUserAgent.page_title;
        delete paramsWithUserAgent.referrer;
      }

      this.log('Creating conversation with params:', paramsWithUserAgent);

      this.siteChannel
        .push('conversation:create', paramsWithUserAgent)
        .receive('ok', (response: any) => {
          this.log('✅ Conversation created:', response);

          const conversation = response.conversation;
          this.conversationId = conversation.conv_id?.toString() || conversation.conversationId;

          // Join conversation channel
          this.joinConversationChannel(this.conversationId!);

          // Notify widget
          this.onConversationCreated?.({
            conversationId: this.conversationId!,
            conv_id: conversation.conv_id,
            status: conversation.status,
            messages: [],
            hasMore: false,
          });

          // Resolve promise with success
          resolve(true);
        })
        .receive('error', (error: any) => {
          this.log('❌ Failed to create conversation:', error);
          this.onError?.(error);
          reject(error);
        })
        .receive('timeout', () => {
          this.log('⏱️ Conversation creation timeout');
          const timeoutError = new Error('Request timeout');
          this.onError?.(timeoutError);
          reject(timeoutError);
        });
    });
  }

  /**
   * Restore existing conversation
   */
  restoreConversation(conversationId: string): void {
    this.log('Restoring conversation:', conversationId);
    this.conversationId = conversationId;
    this.joinConversationChannel(conversationId);
  }

  /**
   * Set conversation ID (called by widget)
   */
  setConversationId(conversationId: string): void {
    this.log('Setting conversation ID:', conversationId);
    this.conversationId = conversationId;
  }

  /**
   * Join conversation (called by widget)
   */
  joinConversation(conversationId: string): void {
    this.log('Joining conversation:', conversationId);
    this.restoreConversation(conversationId);
  }

  /**
   * Join conversation channel
   */
  private joinConversationChannel(conversationId: string): void {
    if (!this.socket) {
      this.log('Cannot join conversation channel: socket not initialized');
      return;
    }

    // Leave previous conversation channel if any
    if (this.conversationChannel) {
      this.conversationChannel.leave();
    }

    const channelName = `conversation:${conversationId}`;
    this.log(`Joining conversation channel: ${channelName}`);

    this.conversationChannel = this.socket.channel(channelName, {});

    this.conversationChannel
      .join()
      .receive('ok', (response) => {
        this.log('✅ Joined conversation channel', response);
        this.isInConversationChannel = true;

        // Process conversation data and messages
        if (response.conversation) {
          const conversation = response.conversation;
          const messages = response.messages || [];

          this.onConversationRestored?.({
            conversationId: conversationId,
            conv_id: conversation.conv_id,
            status: conversation.status,
            messages: messages.map(this.normalizeMessage.bind(this)),
            hasMore: false,
          });
        }

        this.onConversationChannelJoined?.();
      })
      .receive('error', (error) => {
        this.log('❌ Failed to join conversation channel:', error);
        this.onError?.(error);
      });

    // Listen for incoming messages
    this.conversationChannel.on('message:received', (payload) => {
      this.log('📨 Message received:', payload);
      const message = this.normalizeMessage(payload.message);
      this.onMessageReceived?.(message);
    });

    // Listen for translation updates
    this.conversationChannel.on('message:translation_updated', (payload) => {
      this.log('🌐 Translation updated:', payload);

      this.onTranslationUpdated?.({
        msgId: payload.messageId?.toString() || payload.msgId,
        messageId: payload.messageId,
        convId: conversationId,
        translatedText: payload.translatedText,
        translation: {
          translatedText: payload.translatedText,
          targetLanguage: payload.targetLanguage || 'unknown',
          sourceLanguage: payload.sourceLanguage || 'unknown',
        },
      });
    });

    // Listen for message content updates (when agent edits a message)
    this.conversationChannel.on('message:content_updated', (payload) => {
      this.log('✏️ Message content updated:', payload);

      this.onContentUpdated?.({
        msgId: payload.messageId?.toString() || payload.msgId,
        messageId: payload.messageId,
        convId: conversationId,
        content: payload.content,
      });
    });

    // Listen for typing indicators
    this.conversationChannel.on('user:typing', (payload) => {
      this.log('⌨️  User typing:', payload);
      // Can be handled by widget if needed
    });

    // Listen for agent assignment
    this.conversationChannel.on('agent:assigned', (payload) => {
      this.log('👤 Agent assigned:', payload);
      this.onAgentAssigned?.(payload as AssignedAgentData);
    });
  }

  /**
   * Send a message
   */
  sendMessage(text: string, attachmentIds?: number[], conversationId?: string): Promise<void> {
    const convId = conversationId || this.conversationId;

    if (!convId) {
      this.log('Cannot send message: no conversation ID');
      this.onError?.({ reason: 'No conversation ID' });
      return Promise.reject(new Error('No conversation ID'));
    }

    if (!this.conversationChannel) {
      this.log('Cannot send message: not in conversation channel');
      this.onError?.({ reason: 'Not in conversation channel' });
      return Promise.reject(new Error('Not in conversation channel'));
    }

    this.log('Sending message:', { text, attachmentIds });

    const payload: any = {
      text: text,
      conversationId: convId,
    };

    if (attachmentIds && attachmentIds.length > 0) {
      payload.attachmentIds = attachmentIds;
    }

    return new Promise((resolve, reject) => {
      this.conversationChannel!
        .push('message:send', payload, 10_000)
        .receive('ok', (response: unknown) => {
          this.log('✅ Message sent:', response);
          // Message will be received via 'message:received' event
          resolve();
        })
        .receive('error', (error: any) => {
          this.log('❌ Failed to send message:', error);
          this.onError?.(error);
          reject(error instanceof Error ? error : new Error(error?.reason || 'Failed to send message'));
        })
        .receive('timeout', () => {
          const error = new Error('Message send timeout');
          this.log('❌ Message send timeout');
          this.onError?.({ reason: error.message });
          reject(error);
        });
    });
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator(isTyping: boolean, conversationId?: string): void {
    const convId = conversationId || this.conversationId;

    if (!convId || !this.conversationChannel) {
      return;
    }

    const event = isTyping ? 'typing:start' : 'typing:stop';

    this.conversationChannel.push(event, {
      conversationId: convId,
    });
  }

  /**
   * Send typing preview - shows agent what customer is typing in real-time
   * Only sends if typing_preview_enabled is true for the site
   */
  sendTypingPreview(text: string, conversationId?: string): void {
    const convId = conversationId || this.conversationId;

    if (!convId || !this.conversationChannel) {
      return;
    }

    this.conversationChannel.push('typing:preview', {
      conversationId: convId,
      text: text,
    });
  }

  /**
   * Mark messages as read (batch)
   * Used for read receipts - notifies the agent that customer has seen their messages
   */
  markMessagesAsRead(messageIds: number[]): void {
    if (!this.conversationChannel || messageIds.length === 0) {
      return;
    }

    this.log('Marking messages as read:', messageIds);

    this.conversationChannel
      .push('message:read', { message_ids: messageIds })
      .receive('ok', (response) => {
        this.log('✅ Messages marked as read:', response);
      })
      .receive('error', (error) => {
        this.log('❌ Failed to mark messages as read:', error);
      });
  }

  setPageTrackingEnabled(enabled: boolean): void {
    this.pageTrackingEnabled = enabled;
  }

  pushPageChanged(payload: PagePayload): boolean {
    if (!this.conversationChannel || !this.isInConversationChannel) {
      return false;
    }

    this.conversationChannel.push('page:changed', payload);
    return true;
  }

  /**
   * Emit a site-wide page view over the site: channel for superadmin analytics.
   * Deduped by URL so WebSocket reconnects do not re-count the same page-load.
   * Sends NO URL in the payload (privacy: v1 stores only aggregate counts).
   */
  emitSiteView(url: string): boolean {
    if (!this.siteChannel) return false;
    if (url === this.lastSiteViewUrl) return false;
    this.lastSiteViewUrl = url;
    this.siteChannel.push('page:view', {});
    return true;
  }

  /**
   * Normalize message format from Phoenix to widget format
   */
  private normalizeMessage(message: any): SocketMessage {
    // Normalize attachments from backend format to widget format
    const normalizedAttachments = (message.attachments || []).map((att: any) => ({
      id: att.attachment_id || att.id,
      url: att.secure_url || att.url,
      name: att.file_name || att.name,
      type: att.file_type || att.type,
      size: att.file_size || att.size,
    }));

    return {
      id: message.msg_id?.toString() || message.id,
      msg_id: message.msg_id,
      conversationId: message.conv_id?.toString() || message.conversationId,
      conv_id: message.conv_id,
      text: message.body_src || message.text,
      body_src: message.body_src,
      author: message.author,
      author_id: message.author_id,
      timestamp: message.inserted_at ? new Date(message.inserted_at) : new Date(),
      inserted_at: message.inserted_at,
      attachments: normalizedAttachments,
      body_translated: message.body_translated,
      source_language: message.source_language,
      target_language: message.target_language,
      page_visit_id: message.page_visit_id ?? null,
      translation: message.translation,  // ← Add translation from backend
      isTemporary: message.isTemporary || false,
      originalText: message.body_src || message.text,
      // Chatbot message types
      message_type: message.message_type || 'text',
      metadata: message.metadata,
    };
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.log('Disconnecting...');

    if (this.conversationChannel) {
      this.conversationChannel.leave();
      this.conversationChannel = null;
    }

    if (this.siteChannel) {
      this.siteChannel.leave();
      this.siteChannel = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.isInSiteChannel = false;
    this.isInConversationChannel = false;
  }

  /**
   * Check if connected
   */
  public get connected(): boolean {
    return this.isConnected && this.isInSiteChannel;
  }

  /**
   * Get connection state
   */
  public getConnectionState(): { connected: boolean; inRoom: boolean } {
    return {
      connected: this.isConnected && this.isInSiteChannel,
      inRoom: this.isInConversationChannel,
    };
  }

  /**
   * Get current conversation ID
   */
  public getConversationId(): string | null {
    return this.conversationId;
  }

  /**
   * Logging helper
   */
  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[SocketManager]', ...args);
    }
  }
}
